import { Option, some, any, Widget, Action, nothing, IOWidget, mk_pair } from 'widgets-for-react'

import {
  DefaultState,
  StandardBlockImagesLoader,
  StandardBlockToWidget,
  PairAsyncImage,
  AppState,
  CustomPages
} from './state/types.gen'
import { PageTemplateComponent } from './page/widget.gen'
import { router } from './router/widget.gen'
import { SetCurrentWidget } from './state/utils.gen'
import { ExtraParameters } from './router/type.gen'

function Object_values<t extends object>(obj: t) {
  let vals: t[Extract<keyof t, string>][] = []
  for (const prop in obj) {
    vals.push(obj[prop])
  }
  return vals
}

export const EntryClient = <
  CustomBlockData extends { kind: CustomBlockData['kind'] },
  CustomBlocks extends { kind: CustomBlocks['kind'] },
  StandardBlocks,
  CurrentState extends DefaultState<CustomBlockData, CustomBlocks, StandardBlocks, extra_images, CustomPageData, CurrentState>,
  ContentTypes extends keyof CurrentState,
  CustomRoutes extends { kind: ContentTypes },
  extra_images,
  CustomPageData
>(props: {
  rootpath: string
  rootpath_to_ignore: string
  initState: CurrentState
  custom_pages: CustomPages<
    CustomBlockData,
    CustomBlocks,
    StandardBlocks,
    CurrentState,
    ContentTypes,
    CustomRoutes,
    extra_images,
    CustomPageData
  >
  StandardBlockImagesLoader: StandardBlockImagesLoader<CustomBlocks, StandardBlocks, extra_images>
  StandardBlock_To_Widget: StandardBlockToWidget<CustomBlockData, CustomBlocks, StandardBlocks, extra_images, CustomPageData>
  errorPage: IOWidget<CurrentState, never>
  loadingPage: Widget<never>
  acceptAllCookies?: () => void
  getCookie?: (name: string) => string
  videoCookie?: string
  CustomBLockImagesLoader?: (block: CustomBlocks) => Array<PairAsyncImage<extra_images>>
  CustomBlock_To_Widget?: (block: CustomBlocks, block_index:number, extra_data:CustomPageData) => Option<CustomBlockData>
}) => (s0: AppState<CurrentState, CustomRoutes>) =>
  any<Action<AppState<CurrentState, CustomRoutes>>>()([
    //app
    s0.current_widget.v.kind == 'r'
      ? s0.current_widget.v.v(s0.state).map<Action<AppState<CurrentState, CustomRoutes>>>(a => s1 => {
          return {
            ...s1,
            state: a(s1.state)
          }
        })
      : nothing(),
    //router
    router<CurrentState, CustomRoutes>(
      Object_values(props.custom_pages).map(e => e.route),
      props.rootpath,
      props.rootpath_to_ignore
    ).map<Action<AppState<CurrentState, CustomRoutes>>>(newRoute => s1 => {
      let oldRoute = s1.route

      switch (newRoute.kind) {
        case '404':
          return SetCurrentWidget(
            s1,
            newRoute,
            some<IOWidget<CurrentState, Action<CurrentState>>>(_ => props.errorPage(s1.state))
          )

        case 'page': {
          let page = newRoute as { kind: 'page'; slug: string, extra_params:ExtraParameters }
          let widget: IOWidget<CurrentState, Action<CurrentState>> = s2 => {
            return PageTemplateComponent<StandardBlocks, CustomBlocks, CustomBlockData, extra_images, CustomPageData, CurrentState>({
              pageDisplay: p => s2.page.pagedisplay(p, s2),
              Display: b => s2.page.display(b),
              CustomBlock_To_Widget: props.CustomBlock_To_Widget,
              CustomBLockImagesLoader: props.CustomBLockImagesLoader,
              StandardBlockImagesLoader: props.StandardBlockImagesLoader,
              StandardBlock_To_Widget: props.StandardBlock_To_Widget,
              errorPage: props.errorPage(s2),
              loadingPage: props.loadingPage,
              acceptAllCookies: props.acceptAllCookies,
              getCookie: props.getCookie,
              videoCookie: props.videoCookie
            }, s2)({
              ...s2.page,
              page:
                s2.page.page.kind == 'loading' || s2.page.page.kind == 'unloaded'
                  ? s2.page.get_page(page.slug, page.extra_params)
                  : s2.page.page
            } as any).map<Action<CurrentState>>(a => s3 => {
              //@ts-ignore
              return { ...s3, page: a(s3.page) }
            })
          }

          return SetCurrentWidget(s1, newRoute, some(widget))
        }
        case 'ignore': {
          let widget: IOWidget<CurrentState, Action<CurrentState>> = _ => nothing()
          return { ...s1, current_widget: some(widget), route: newRoute }
        }

        // custom pages
        default: {
          let currentCustomPage = props.custom_pages[newRoute.kind]
          let widget: IOWidget<CurrentState, Action<CurrentState>> = s2 =>
            currentCustomPage
              .widget(mk_pair(newRoute, currentCustomPage.getState(s2)))
              .map<Action<CurrentState>>(a => s3 => currentCustomPage.setState(s3, a(currentCustomPage.getState(s3))))
          return {
            ...s1,
            route: newRoute,
            current_widget: some(widget),
            state: currentCustomPage.updateStateBasesOnRoute(s1.state, newRoute)
          }
        }
      }
    })
  ])
